import { useEffect, useRef, useState } from 'react';
import { fetchWithAuth } from '../lib/api';

interface OutlineChapter {
  chapterIndex: number;
  areaTitle: string;
  sessionCount: number;
  themes: string[];
  titleSuggestion?: string;
}

interface OutlineData {
  chapters: OutlineChapter[];
  bookTitleSuggestions?: string[];
}

interface ConversationTurn {
  role: 'user' | 'assistant';
  content: string;
}

interface BookPhoto {
  photoId: string;
  viewUrl: string | null;
  status: string;
  linkedMemoryIds?: string[];
}

interface SessionMeta {
  sessionId: string;
  areaTags?: string[];
}

interface CurriculumArea {
  id: string;
  title: string;
}

interface OutlineReviewProps {
  jobId: string;
  restUrl: string;
  onApproved: () => void;
  onCancelled: () => void;
  onTitleSuggestions?: (suggestions: string[]) => void;
}

const MAX_THUMBNAILS = 5;

function PhotoThumbnails({ photos }: { photos: BookPhoto[] }) {
  const visible = photos.slice(0, MAX_THUMBNAILS);
  const overflow = photos.length - MAX_THUMBNAILS;
  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-2">
      {visible.map((photo) =>
        photo.viewUrl ? (
          <img
            key={photo.photoId}
            src={photo.viewUrl}
            alt="Photo in this chapter"
            className="w-10 h-10 rounded-md object-cover border border-stone-200 flex-shrink-0"
          />
        ) : (
          <div
            key={photo.photoId}
            className="w-10 h-10 rounded-md bg-stone-100 border border-stone-200 flex items-center justify-center flex-shrink-0"
            title="Photo (preview unavailable)"
          >
            <svg className="w-4 h-4 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4-4a3 3 0 014.243 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M6 8h.01M4 6h16a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2z" />
            </svg>
          </div>
        ),
      )}
      {overflow > 0 && (
        <span className="text-xs text-stone-400 font-medium">+{overflow} more</span>
      )}
    </div>
  );
}

/** Build a map of areaTitle → photos for that chapter.
 *  Chain: photo.linkedMemoryIds → session.areaTags[0] → area.id → area.title → chapter.areaTitle
 */
function buildChapterPhotoMap(
  photos: BookPhoto[],
  sessions: SessionMeta[],
  areas: CurriculumArea[],
): Map<string, BookPhoto[]> {
  // sessionId → first areaTag
  const sessionToAreaId = new Map<string, string>();
  for (const s of sessions) {
    if (s.areaTags && s.areaTags.length > 0) {
      sessionToAreaId.set(s.sessionId, s.areaTags[0]);
    }
  }

  // areaId → areaTitle
  const areaIdToTitle = new Map<string, string>();
  for (const a of areas) {
    areaIdToTitle.set(a.id, a.title);
  }

  const result = new Map<string, BookPhoto[]>();
  for (const photo of photos) {
    if (!photo.viewUrl) continue; // skip photos without a presigned URL
    const linked = photo.linkedMemoryIds ?? [];
    const placed = new Set<string>(); // avoid placing same photo in same chapter twice
    for (const sessionId of linked) {
      const areaId = sessionToAreaId.get(sessionId);
      if (!areaId) continue;
      const areaTitle = areaIdToTitle.get(areaId);
      if (!areaTitle || placed.has(areaTitle)) continue;
      placed.add(areaTitle);
      const bucket = result.get(areaTitle) ?? [];
      bucket.push(photo);
      result.set(areaTitle, bucket);
    }
  }
  return result;
}

export function OutlineReview({ jobId, restUrl, onApproved, onCancelled, onTitleSuggestions }: OutlineReviewProps) {
  const [outline, setOutline] = useState<OutlineData | null>(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Per-chapter photo map (areaTitle → photos)
  const [chapterPhotoMap, setChapterPhotoMap] = useState<Map<string, BookPhoto[]>>(new Map());

  const [conversation, setConversation] = useState<ConversationTurn[]>([]);
  const [message, setMessage] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const chatBottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchWithAuth(`${restUrl}outline/${jobId}`)
      .then(res => {
        if (!res.ok) throw new Error('Could not load outline');
        return res.json() as Promise<{ outline: OutlineData }>;
      })
      .then(data => {
        setOutline(data.outline);
        if (data.outline.bookTitleSuggestions?.length && onTitleSuggestions) {
          onTitleSuggestions(data.outline.bookTitleSuggestions);
        }
      })
      .catch(() => setError('Could not load your book outline. Please try again.'))
      .finally(() => setLoading(false));
  }, [jobId, restUrl, onTitleSuggestions]);

  useEffect(() => {
    // Fetch photos, sessions, and curriculum in parallel to build per-chapter photo map.
    // All failures are non-fatal — outline still works without photos.
    const REST_BASE = restUrl.replace(/\/$/, '');
    Promise.all([
      fetchWithAuth(`${REST_BASE}/photos`)
        .then(r => r.ok ? r.json() as Promise<{ photos: BookPhoto[] }> : Promise.reject())
        .then(d => d.photos),
      fetchWithAuth(`${REST_BASE}/sessions`)
        .then(r => r.ok ? r.json() as Promise<{ sessions: SessionMeta[] }> : Promise.reject())
        .then(d => d.sessions),
      fetchWithAuth(`${REST_BASE}/curriculum`)
        .then(r => r.ok ? r.json() as Promise<{ areas: CurriculumArea[] }> : Promise.reject())
        .then(d => d.areas),
    ]).then(([photos, sessions, areas]) => {
      setChapterPhotoMap(buildChapterPhotoMap(photos, sessions, areas));
    }).catch(() => { /* non-fatal */ });
  }, [restUrl]);

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation]);

  const handleRefine = async () => {
    const trimmed = message.trim();
    if (!trimmed || chatLoading) return;
    setChatLoading(true);
    setChatError(null);
    setMessage('');

    const userTurn: ConversationTurn = { role: 'user', content: trimmed };
    setConversation(prev => [...prev, userTurn]);

    try {
      const res = await fetchWithAuth(`${restUrl}outline/${jobId}/refine`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: trimmed, conversationHistory: conversation }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Request failed' })) as { error?: string };
        throw new Error(err.error ?? 'Request failed');
      }
      const data = await res.json() as { outline: OutlineData; reply: string };
      setOutline(data.outline);
      if (data.outline.bookTitleSuggestions?.length && onTitleSuggestions) {
        onTitleSuggestions(data.outline.bookTitleSuggestions);
      }
      setConversation(prev => [...prev, { role: 'assistant', content: data.reply }]);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not refine outline. Try again.';
      setChatError(msg);
      setConversation(prev => prev.slice(0, -1)); // remove the user turn on error
      setMessage(trimmed); // restore input
    } finally {
      setChatLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !chatLoading) void handleRefine();
  };

  const handleApprove = async () => {
    setApproving(true);
    setError(null);
    try {
      const res = await fetchWithAuth(`${restUrl}outline/${jobId}/approve`, { method: 'POST' });
      if (!res.ok) throw new Error('Approval failed');
      onApproved();
    } catch {
      setError('Could not approve outline. Please try again.');
      setApproving(false);
    }
  };

  const handleCancel = async () => {
    try {
      await fetchWithAuth(`${restUrl}outline/${jobId}`, { method: 'DELETE' });
    } catch {
      // ignore
    }
    onCancelled();
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8 text-center">
        <p className="text-stone-400 text-sm">Loading your book outline...</p>
      </div>
    );
  }

  if (error && !outline) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8">
        <p className="text-sm text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6">
        <h2 className="text-xl font-serif text-stone-900">Your book outline is ready.</h2>
        <p className="text-sm text-stone-500 mt-1">
          Review the chapters below. Ask me to make changes, then approve when you're happy.
        </p>
      </div>

      {/* Chapter cards */}
      <div className="space-y-3">
        {(outline?.chapters ?? []).map((chapter, i) => {
          const chapterPhotos = chapterPhotoMap.get(chapter.areaTitle) ?? [];
          return (
            <div key={chapter.chapterIndex} className="bg-white rounded-xl border border-stone-200 p-4">
              <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide">
                Chapter {i + 1}
              </p>
              <p className="text-base font-semibold text-stone-800 mt-0.5">
                {chapter.titleSuggestion ?? chapter.areaTitle}
              </p>
              <p className="text-xs text-stone-500 mt-0.5">
                {chapter.sessionCount} session{chapter.sessionCount !== 1 ? 's' : ''}
                {chapterPhotos.length > 0 && ` · ${chapterPhotos.length} photo${chapterPhotos.length !== 1 ? 's' : ''}`}
              </p>
              {chapter.themes.length > 0 && (
                <ul className="mt-2 space-y-0.5">
                  {chapter.themes.map((theme, ti) => (
                    <li key={ti} className="text-sm text-stone-600">
                      <span aria-hidden="true">· </span>{theme}
                    </li>
                  ))}
                </ul>
              )}
              {chapterPhotos.length > 0 && (
                <PhotoThumbnails photos={chapterPhotos} />
              )}
            </div>
          );
        })}
      </div>

      {/* Chat panel */}
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-stone-100">
          <p className="text-sm font-semibold text-stone-700">Refine with AI</p>
          <p className="text-xs text-stone-400 mt-0.5">
            "Rename Chapter 2 to Early Adventures" · "Merge chapters 3 and 4" · "Put the military chapter first"
          </p>
        </div>

        {/* Conversation thread */}
        {conversation.length > 0 && (
          <div className="px-4 py-3 space-y-2 max-h-64 overflow-y-auto">
            {conversation.map((turn, i) => (
              <div
                key={i}
                className={`text-sm rounded-lg px-3 py-2 max-w-prose ${
                  turn.role === 'user'
                    ? 'bg-stone-100 text-stone-800 ml-auto text-right'
                    : 'bg-amber-50 text-stone-700'
                }`}
              >
                {turn.content}
              </div>
            ))}
            {chatLoading && (
              <div className="text-sm text-stone-400 italic px-3 py-1">Thinking...</div>
            )}
            <div ref={chatBottomRef} />
          </div>
        )}

        {chatError && (
          <div className="px-4 pb-2">
            <p className="text-xs text-red-600">{chatError}</p>
          </div>
        )}

        {/* Input */}
        <div className="px-4 py-3 border-t border-stone-100 flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={message}
            onChange={e => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask to rename, reorder, or merge chapters..."
            disabled={chatLoading}
            className="flex-1 rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent disabled:opacity-50"
          />
          <button
            type="button"
            onClick={() => void handleRefine()}
            disabled={chatLoading || !message.trim()}
            className="px-4 py-2 rounded-lg bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {chatLoading ? '...' : 'Send'}
          </button>
        </div>
      </div>

      {/* Action bar */}
      {error && (
        <p className="text-sm text-red-700">{error}</p>
      )}
      <div className="flex gap-3 pt-1">
        <button
          type="button"
          onClick={handleApprove}
          disabled={approving || chatLoading}
          className="px-6 py-3 rounded-xl bg-amber-500 text-white font-semibold hover:bg-amber-600 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 text-sm disabled:opacity-50"
        >
          {approving ? 'Approving...' : 'Approve & Write'}
        </button>
        <button
          type="button"
          onClick={handleCancel}
          className="px-4 py-3 rounded-xl border border-stone-300 text-stone-700 text-sm font-semibold hover:bg-stone-50 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
